/* eslint-disable react/no-unescaped-entities */
import { Box, Button, Typography } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import FacebookIcon from '@material-ui/icons/Facebook';
import LockOutLineIcon from '@material-ui/icons/LockOutlined';
import MailOutLineIcon from '@material-ui/icons/MailOutline';
import { Alert } from '@material-ui/lab';
import { ReactComponent as GoogleIcon } from 'assets/social-icons/google.svg';
import { DividerWithText, Link, LoadingButton } from 'components';
import { FB_CLIENT_ID, GOOGLE_CLIENT_ID } from 'config';
import { useAuth } from 'context/AuthContext';
import { useNotifications } from 'context/NotificationsContext';
import { Field, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import {
    MatrixAuthData,
    useFacebookLoginMutation,
    useGoogleLoginMutation,
    useSendVerifyEmailMutation,
    useSignInMutation,
} from 'graphql/generated';
import jwtDecode from 'jwt-decode';
import React, { useEffect, useState } from 'react';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import { GoogleLogin, GoogleLoginResponse } from 'react-google-login';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import browserHistory from 'utils/browser-utils';
import { useStyle } from './signinForm.style';
import { SigninSchema } from './validationSchema';

type SigninFormProps = {
    pageUsage?: boolean;
};

export const SignInForm: React.FC<SigninFormProps> = ({ pageUsage = false }) => {
    const classes = useStyle();
    const { t } = useTranslation();
    const { closeModal, saveAuthData, selectTab, email, isLoggedIn } = useAuth();
    const { notify } = useNotifications();
    const { pathname, state } = useLocation<{ from: { pathname: string }; redirected: boolean }>();

    const [hasVerifyError, setHasVerifyError] = useState(false);

    const initialValues = {
        password: '',
        email: email || '',
    };

    const path = state?.redirected ? state.from.pathname : '/';

    const onLoginSuccess = (accessToken: string, matrixAuth?: MatrixAuthData | null): void => {
        const user = jwtDecode(accessToken);
        saveAuthData({ accessToken, matrixAuth, user });
        if (pageUsage) {
            browserHistory.push(path);
        } else {
            closeModal();
        }

        notify({
            type: 'success',
            message: `${t('signIn:welcomeMessage')}`,
        });
        if (pathname === '/reset-password' || pathname === '/sign-in') {
            browserHistory.push('/');
        }
    };

    const [logIn, { loading, error }] = useSignInMutation({
        onCompleted({ login: { accessToken, matrixAuth } }) {
            onLoginSuccess(accessToken, matrixAuth);
        },
        onError(err) {
            if (err.message === 'EMAIL_NOT_VERIFIED') {
                setHasVerifyError(true);
            } else {
                setHasVerifyError(false);
            }
        },
    });

    const [facebookLogin] = useFacebookLoginMutation({
        onCompleted({ facebookLogin: { accessToken, matrixAuth } }) {
            onLoginSuccess(accessToken, matrixAuth);
        },
    });

    const [googleLogin] = useGoogleLoginMutation({
        onCompleted({ googleLogin: { accessToken, matrixAuth } }) {
            onLoginSuccess(accessToken, matrixAuth);
        },
    });

    const [sendVerifyEmail] = useSendVerifyEmailMutation({
        onCompleted() {
            notify({
                type: 'success',
                message: t('signIn:verifyEmailSent'),
            });
        },
        onError(error) {
            notify({
                type: 'error',
                message: t(`error:${error.message}`),
            });
        },
    });

    const onSubmit = (formData): void => {
        logIn({
            variables: {
                credentials: {
                    email: formData.email,
                    password: formData.password,
                },
            },
        });
    };

    useEffect(() => {
        if (pageUsage && isLoggedIn) {
            browserHistory.push('/');
        }
    }, []);

    return (
        <div className={classes.paper}>
            <FacebookLogin
                appId={FB_CLIENT_ID}
                size="large"
                disableMobileRedirect={true}
                autoLoad={false}
                scope="public_profile, email"
                render={(renderProps) => (
                    <Button fullWidth className={classes.facebook} onClick={renderProps.onClick}>
                        <FacebookIcon style={{ color: '#fff' }} />
                        <Box flexGrow={1}>
                            <Typography variant="h5">{t('signIn:withFacebook')}</Typography>
                        </Box>
                    </Button>
                )}
                callback={(response) => {
                    facebookLogin({
                        context: {
                            headers: {
                                'X-FB-Access-Token': response.accessToken,
                            },
                        },
                    });
                }}
            />

            <GoogleLogin
                clientId={GOOGLE_CLIENT_ID}
                cookiePolicy="single_host_origin"
                autoLoad={false}
                render={(renderProps) => (
                    <Button fullWidth className={classes.google} onClick={renderProps.onClick}>
                        <GoogleIcon style={{ width: 23, height: 23 }} />
                        <Box flexGrow={1}>
                            <Typography variant="h5">{t('signIn:withGoogle')}</Typography>
                        </Box>
                    </Button>
                )}
                onSuccess={(response) => {
                    if (!response.code) {
                        googleLogin({
                            context: {
                                headers: {
                                    'X-Google-Access-Token': (response as GoogleLoginResponse).accessToken,
                                },
                            },
                        });
                    }
                }}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                onFailure={(): void => {}}
            />

            <DividerWithText>
                <Typography variant="h6" style={{ fontWeight: 'bold', opacity: 0.6 }}>
                    {t('signIn:or')}
                </Typography>
            </DividerWithText>

            <Formik initialValues={initialValues} validationSchema={SigninSchema} onSubmit={onSubmit}>
                {({ handleSubmit, values }): any => {
                    return (
                        <>
                            {email && (
                                <Alert style={{ width: '100%' }} severity="info">
                                    {t('signIn:verifyEmailSent')}
                                </Alert>
                            )}
                            {hasVerifyError && (
                                <Alert style={{ width: '100%' }} severity="warning">
                                    {t('signIn:verifyYourEmail')}.&nbsp;
                                    <Link
                                        className={classes.verify}
                                        link
                                        to="#"
                                        onClick={() =>
                                            sendVerifyEmail({
                                                variables: { input: { email: values.email } },
                                            })
                                        }
                                    >
                                        {t('signIn:resendEmail')}
                                    </Link>
                                </Alert>
                            )}
                            <form onSubmit={handleSubmit} className={classes.form}>
                                <Field
                                    component={TextField}
                                    variant="outlined"
                                    fullWidth
                                    disabled={loading}
                                    id="email"
                                    label={t('signIn:emailAddress')}
                                    name="email"
                                    margin="normal"
                                    autoComplete="off"
                                    autoFocus
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <MailOutLineIcon className={classes.inputIcon} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{
                                        autoCapitalize: 'none',
                                    }}
                                />
                                <Field
                                    component={TextField}
                                    variant="outlined"
                                    fullWidth
                                    disabled={loading}
                                    name="password"
                                    label={t('signIn:password')}
                                    type="password"
                                    id="password"
                                    autoComplete="current-password"
                                    margin="normal"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockOutLineIcon className={classes.inputIcon} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{
                                        autoCapitalize: 'none',
                                    }}
                                />
                                <Link
                                    link
                                    to={pageUsage ? '/forgot-password' : '#'}
                                    onClick={() => {
                                        if (!pageUsage) {
                                            selectTab(2);
                                        }
                                    }}
                                >
                                    <Typography variant="h6">{t('signIn:forgotPassword')}</Typography>
                                </Link>
                                {error && (
                                    <Alert style={{ width: '100%' }} severity="error">
                                        {t(`error:${error.message}`)}
                                    </Alert>
                                )}
                                <LoadingButton
                                    fullWidth
                                    type="submit"
                                    loading={loading}
                                    variant="contained"
                                    color="primary"
                                    className={classes.submit}
                                    loadingText={t('signIn:loggingIn')}
                                    text={t('signIn:logIn')}
                                />
                            </form>
                        </>
                    );
                }}
            </Formik>
        </div>
    );
};
